import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Skeleton,
  Alert,
} from '@mui/joy';
import { Container } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getShipmentById } from '../../main/API/shipment';
import { getShipmentTrackingPage } from '../../main/API/trackingEvent';
import { getTimelineEventTitle, isTimelineStatusChipRedundant } from '../../main/utils/trackingEventLabels';
import { getLocationDisplayName } from '../../main/utils/locationDisplayName';
import { translateApiErrorData } from '../../main/utils/masterApiError';
import { dedupeRequest } from '../../main/utils/dedupeRequest';
import PiecesStatusSummaryChips from '../../main/widgets/PiecesStatusSummaryChips';
import {
  shipmentStatusChipSx,
  shipmentStatusJoyColor,
  shipmentStatusLabel,
} from '../../main/utils/shipmentStatusDisplay';

function formatEventDate(isoString) {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return '—';
  }
}

function formatEtaDate(dateString) {
  if (!dateString) return '—';
  try {
    const [y, m, d] = dateString.split('-').map(Number);
    if (!y || !m || !d) return dateString;
    const date = new Date(y, m - 1, d);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (_) {
    return dateString;
  }
}

export default function ShipmentTrackingContent({
  shipmentId,
  variant = 'page',
  onBack,
}) {
  const { t, i18n } = useTranslation('ui');
  const tRef = useRef(t);
  tRef.current = t;
  const [events, setEvents] = useState([]);
  const [expectedDestinationEta, setExpectedDestinationEta] = useState(null);
  const [expectedWarehouseEta, setExpectedWarehouseEta] = useState(null);
  const [piecesStatusSummary, setPiecesStatusSummary] = useState(null);
  const [shipmentUnit, setShipmentUnit] = useState(null);
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const clearTrackingPayload = useCallback(() => {
    setEvents([]);
    setExpectedDestinationEta(null);
    setExpectedWarehouseEta(null);
    setPiecesStatusSummary(null);
    setShipmentUnit(null);
  }, []);

  const fetchTracking = useCallback(async ({ dedupeKey, isCancelled } = {}) => {
    if (isCancelled && isCancelled()) return;
    if (!shipmentId) {
      setError(tRef.current('customerTracking.invalidShipment'));
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const doFetch = () => Promise.allSettled([
        getShipmentTrackingPage(shipmentId),
        getShipmentById(shipmentId),
      ]);
      const [trackingRes, shipmentRes] = dedupeKey
        ? await dedupeRequest(dedupeKey, doFetch)
        : await doFetch();
      if (isCancelled && isCancelled()) return;
      setLoading(false);
      if (trackingRes.status === 'fulfilled' && trackingRes.value && trackingRes.value.data) {
        const payload = trackingRes.value.data;
        const unit =
          payload.shipment_unit && typeof payload.shipment_unit === 'object'
            ? payload.shipment_unit
            : null;
        setEvents(Array.isArray(payload.tracking_events) ? payload.tracking_events : []);
        setShipmentUnit(unit);
        setExpectedDestinationEta(
          payload.expected_destination_eta || unit?.expected_destination_eta || null
        );
        setExpectedWarehouseEta(
          payload.expected_warehouse_eta || unit?.expected_warehouse_eta || null
        );
        setPiecesStatusSummary(payload.pieces_status_summary || null);
      } else {
        clearTrackingPayload();
      }
      if (shipmentRes.status === 'fulfilled' && shipmentRes.value && shipmentRes.value.data) {
        setShipment(shipmentRes.value.data);
      } else {
        setShipment(null);
      }
      if (trackingRes.status === 'rejected') throw trackingRes.reason;
    } catch (err) {
      if (isCancelled && isCancelled()) return;
      setLoading(false);
      const response = err && err.response;
      if (!response) {
        setError(tRef.current('trackingEvent.loadFailed'));
        clearTrackingPayload();
        return;
      }
      if (response.status === 404) {
        setError(tRef.current('customerTracking.notFound'));
        clearTrackingPayload();
        return;
      }
      if (response.status >= 400) {
        setError((response.data && translateApiErrorData(response.data, tRef.current)) || tRef.current('trackingEvent.loadFailed'));
        clearTrackingPayload();
        return;
      }
      setError(tRef.current('trackingEvent.loadFailed'));
      clearTrackingPayload();
    }
  }, [shipmentId, clearTrackingPayload]);

  const fetchTrackingRef = useRef(fetchTracking);
  fetchTrackingRef.current = fetchTracking;

  useEffect(() => {
    let cancelled = false;
    if (!shipmentId) return undefined;
    fetchTrackingRef.current({
      dedupeKey: `tracking/shipment:${shipmentId}`,
      isCancelled: () => cancelled,
    });
    return () => {
      cancelled = true;
    };
  }, [shipmentId]);

  useEffect(() => {
    if (variant !== 'page') return undefined;
    window.scrollTo(0, 0);
    return undefined;
  }, [shipmentId, variant]);

  const showShipmentUnitSection = !error && !loading;
  const hasAnyEta = !!(expectedWarehouseEta || expectedDestinationEta);
  const unitSerial =
    shipmentUnit?.serial_number != null && String(shipmentUnit.serial_number).trim() !== ''
      ? String(shipmentUnit.serial_number).trim()
      : null;
  const unitTrackingUrl =
    shipmentUnit?.tracking_url != null && String(shipmentUnit.tracking_url).trim() !== ''
      ? String(shipmentUnit.tracking_url).trim()
      : null;
  const originWarehouseName = getLocationDisplayName(shipmentUnit?.origin_warehouse, i18n.language);
  const destinationWarehouseName = getLocationDisplayName(shipmentUnit?.destination_warehouse, i18n.language);
  const hasUnitDetails = !!(
    unitSerial ||
    unitTrackingUrl ||
    originWarehouseName ||
    destinationWarehouseName
  );

  const cardBg = '#ffffff';
  const cardRadius = '16px';
  const cardShadow = '0 2px 12px rgba(0,0,0,0.06)';
  const textPrimary = '#1e293b';
  const textSecondary = '#64748b';
  const textMuted = '#94a3b8';
  const timelineColor = '#e2e8f0';
  const timelineDotDone = '#22c55e';
  const timelineDotCurrent = '#4f46e5';
  const isModal = variant === 'modal';
  const Wrapper = isModal ? Box : Container;

  if (loading && events.length === 0) {
    return (
      <Wrapper maxWidth={isModal ? undefined : 'md'}>
        <Skeleton variant="rectangular" height={40} width={200} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: cardRadius, mb: 3 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" height={88} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper maxWidth={isModal ? undefined : 'md'}>
      {!isModal && typeof onBack === 'function' && (
        <Button
          variant="plain"
          size="sm"
          startDecorator={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 2, color: textSecondary, '&:hover': { color: textPrimary } }}
        >
          {t('actions.back')}
        </Button>
      )}

      <Card
        variant="plain"
        sx={{
          bgcolor: cardBg,
          boxShadow: cardShadow,
          borderRadius: cardRadius,
          border: '1px solid #e2e8f0',
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                bgcolor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalShippingIcon sx={{ color: '#4f46e5', fontSize: 26 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography level="title-lg" sx={{ color: textPrimary, fontWeight: 600 }}>
                {t('trackingEvent.shipmentTrackingTitle', { id: shipmentId })}
              </Typography>
              <Typography level="body2" sx={{ color: textMuted }}>
                {t('customerTracking.trackingTimeline')}
              </Typography>
            </Box>
            {shipment && shipment.status && (
              <Chip
                size="md"
                color={shipmentStatusJoyColor(shipment.status)}
                variant="soft"
                sx={shipmentStatusChipSx(shipment.status)}
              >
                {shipmentStatusLabel(shipment.status, t)}
              </Chip>
            )}
          </Box>
          {piecesStatusSummary && (
            <Box sx={{ mt: 1.5 }}>
              <Typography level="body2" sx={{ color: textMuted, mb: 0.75 }}>
                {t('customerTracking.piecesStatus')}
              </Typography>
              <PiecesStatusSummaryChips summary={piecesStatusSummary} />
            </Box>
          )}
        </CardContent>
      </Card>

      {showShipmentUnitSection && (
        <Card
          variant="plain"
          sx={{
            bgcolor: cardBg,
            boxShadow: cardShadow,
            borderRadius: cardRadius,
            border: '1px solid #e2e8f0',
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  bgcolor: '#eef2ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Inventory2OutlinedIcon sx={{ color: '#4f46e5', fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="title-sm" sx={{ color: textPrimary, fontWeight: 600, mb: 1 }}>
                  {t('customerTracking.shipmentUnit')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {unitSerial && (
                    <Box>
                      <Typography level="body2" sx={{ color: textMuted, mb: 0.25 }}>
                        {t('customerTracking.unitSerial')}
                      </Typography>
                      <Typography level="title-sm" sx={{ color: textPrimary, fontWeight: 600 }}>
                        {unitSerial}
                      </Typography>
                    </Box>
                  )}
                  {(originWarehouseName || destinationWarehouseName) && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 2 },
                      }}
                    >
                      {originWarehouseName && (
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography level="body2" sx={{ color: textMuted, mb: 0.25 }}>
                            {t('customerTracking.originWarehouse')}
                          </Typography>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: textMuted }} />
                            <Typography level="title-sm" sx={{ color: textPrimary, fontWeight: 600 }}>
                              {originWarehouseName}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {destinationWarehouseName && (
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography level="body2" sx={{ color: textMuted, mb: 0.25 }}>
                            {t('customerTracking.destinationWarehouse')}
                          </Typography>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: textMuted }} />
                            <Typography level="title-sm" sx={{ color: textPrimary, fontWeight: 600 }}>
                              {destinationWarehouseName}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                  <Box>
                    <Typography
                      level="body2"
                      sx={{
                        color: textMuted,
                        mb: 0.75,
                        mt: hasUnitDetails ? 0.5 : 0,
                        fontWeight: 600,
                      }}
                    >
                      {t('customerTracking.expectedArrival')}
                    </Typography>
                    {hasAnyEta ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <Typography level="body2" sx={{ color: textMuted, mb: 0.25 }}>
                            {t('shipmentUnit.colEtaDestination')}
                          </Typography>
                          <Typography
                            level="title-sm"
                            sx={{
                              color: expectedDestinationEta ? textPrimary : textSecondary,
                              fontWeight: expectedDestinationEta ? 600 : 500,
                              fontStyle: expectedDestinationEta ? 'normal' : 'italic',
                            }}
                          >
                            {expectedDestinationEta
                              ? formatEtaDate(expectedDestinationEta)
                              : t('customerTracking.etaToBeConfirmed')}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography level="body2" sx={{ color: textMuted, mb: 0.25 }}>
                            {t('shipmentUnit.colEtaWarehouse')}
                          </Typography>
                          <Typography
                            level="title-sm"
                            sx={{
                              color: expectedWarehouseEta ? textPrimary : textSecondary,
                              fontWeight: expectedWarehouseEta ? 600 : 500,
                              fontStyle: expectedWarehouseEta ? 'normal' : 'italic',
                            }}
                          >
                            {expectedWarehouseEta
                              ? formatEtaDate(expectedWarehouseEta)
                              : t('customerTracking.etaToBeConfirmed')}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Typography level="body2" sx={{ color: textSecondary, mb: 0.5 }}>
                          {t('customerTracking.etaNotAvailableYet')}
                        </Typography>
                        <Typography level="body2" sx={{ color: textMuted, fontSize: '0.8125rem' }}>
                          {t('customerTracking.etaNotAvailableHint')}
                        </Typography>
                      </>
                    )}
                  </Box>
                  {unitTrackingUrl && (
                    <Button
                      component="a"
                      href={unitTrackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="soft"
                      color="primary"
                      size="sm"
                      endDecorator={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        mt: 0.5,
                        alignSelf: 'flex-start',
                        borderRadius: '10px',
                      }}
                    >
                      {t('customerTracking.openCarrierTracking')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert
          color="danger"
          variant="soft"
          sx={{ mb: 3, borderRadius: '12px' }}
          endDecorator={
            <Button variant="soft" color="danger" size="sm" onClick={fetchTracking}>
              {t('actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!error && events.length === 0 && (
        <Card
          variant="plain"
          sx={{
            bgcolor: cardBg,
            boxShadow: cardShadow,
            borderRadius: cardRadius,
            border: '1px solid #e2e8f0',
          }}
        >
          <CardContent sx={{ py: 4, textAlign: 'center' }}>
            <Typography level="body1" sx={{ color: textSecondary }}>
              {t('customerTracking.noEvents')}
            </Typography>
            <Typography level="body2" sx={{ color: textMuted, mt: 0.5 }}>
              {t('customerTracking.noEventsHint')}
            </Typography>
            <Button variant="soft" color="primary" size="sm" onClick={fetchTracking} sx={{ mt: 2 }}>
              {t('actions.refresh')}
            </Button>
          </CardContent>
        </Card>
      )}

      {!error && events.length > 0 && (
        <Card
          variant="plain"
          sx={{
            bgcolor: cardBg,
            boxShadow: cardShadow,
            borderRadius: cardRadius,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ position: 'relative', pl: 3, pb: 1 }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 10,
                  top: 20,
                  bottom: 20,
                  width: 2,
                  bgcolor: timelineColor,
                  borderRadius: 1,
                }}
              />
              {events.map((evt, index) => {
                const isFirst = index === 0;
                const isLast = index === events.length - 1;
                const eventTitle = getTimelineEventTitle(evt);
                const showStatusChip = !isTimelineStatusChipRedundant(
                  evt,
                  eventTitle,
                  (status) => shipmentStatusLabel(status, t),
                );
                return (
                  <Box
                    key={evt.id}
                    sx={{
                      position: 'relative',
                      py: 2,
                      pr: 2,
                      borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -22,
                        top: 26,
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        bgcolor: isFirst ? timelineDotCurrent : timelineDotDone,
                        border: '3px solid',
                        borderColor: cardBg,
                        boxShadow: `0 0 0 1px ${isFirst ? timelineDotCurrent : timelineDotDone}`,
                        zIndex: 1,
                      }}
                    />
                    <Box sx={{ pl: 0.5 }}>
                      <Typography level="title-sm" sx={{ color: textPrimary, fontWeight: 600, mb: 0.25 }}>
                        {eventTitle}
                      </Typography>
                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.8125rem', mb: 0.5 }}>
                        {formatEventDate(evt.timestamp)}
                      </Typography>
                      {evt.description && (
                        <Typography level="body2" sx={{ color: textSecondary, fontSize: '0.875rem', mt: 0.5 }}>
                          {evt.description}
                        </Typography>
                      )}
                      {(evt.location_name || showStatusChip) && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mt: 0.75,
                            flexWrap: 'wrap',
                          }}
                        >
                          {evt.location_name && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOnIcon sx={{ fontSize: 16, color: textMuted }} />
                              <Typography level="body2" sx={{ color: textSecondary, fontSize: '0.875rem' }}>
                                {evt.location_name}
                              </Typography>
                            </Box>
                          )}
                          {showStatusChip && (
                            <Chip
                              size="sm"
                              color={shipmentStatusJoyColor(evt.status_snapshot)}
                              variant="soft"
                              sx={shipmentStatusChipSx(evt.status_snapshot)}
                            >
                              {shipmentStatusLabel(evt.status_snapshot, t)}
                            </Chip>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}
    </Wrapper>
  );
}
